<script lang="ts">
  import ChromeFrame from './ChromeFrame.svelte';
  import SafariFrame from './SafariFrame.svelte';

  interface Props {
    /** Image src — typically a full-page capture under /shared/images/screenshots/. */
    src: string;
    /** Alt text. */
    alt: string;
    /** Browser frame style. */
    frame?: 'chrome' | 'safari';
    theme?: 'light' | 'dark';
    url?: string;
    tabTitle?: string;
    secure?: boolean;
    flat?: boolean;
    /** Chrome density. 'md' is true-to-Chrome (~86px header); 'sm' compacts the
     *  chrome so it takes less proportional space when rendered large.
     *  Currently only the Chrome frame honors this. */
    size?: 'sm' | 'md';
    /** 0..1 — fraction of the way through a long page capture, used to translate
     *  the inner image upward as if the user were scrolling. 0 = top, 1 = bottom. */
    scroll?: number;
    /** Aspect ratio of the visible content viewport (excluding the chrome).
     *  Default 16/10 matches a typical browser window screenshot crop. */
    contentAspect?: string;
    class?: string;
    imgLoading?: 'eager' | 'lazy';
  }

  let {
    src,
    alt,
    frame = 'chrome',
    theme = 'light',
    url = 'app.corral.ai',
    tabTitle = 'Corral',
    secure = true,
    flat = false,
    size = 'md',
    scroll = 0,
    contentAspect = '16 / 10',
    class: className = '',
    imgLoading = 'lazy',
  }: Props = $props();

  let imgEl: HTMLImageElement | null = $state(null);
  let imgNaturalH = $state(0);
  let imgNaturalW = $state(0);
  let viewportEl: HTMLDivElement | null = $state(null);
  let viewportH = $state(0);
  let viewportW = $state(0);

  function onImgLoad() {
    if (imgEl) {
      imgNaturalH = imgEl.naturalHeight;
      imgNaturalW = imgEl.naturalWidth;
    }
  }

  $effect(() => {
    if (!viewportEl) return;
    const ro = new ResizeObserver(() => {
      if (viewportEl) {
        viewportH = viewportEl.clientHeight;
        viewportW = viewportEl.clientWidth;
      }
    });
    ro.observe(viewportEl);
    return () => ro.disconnect();
  });

  // Scaled image height when rendered at the viewport's width (object-fit: contain on width).
  const scaledImgH = $derived(
    imgNaturalW > 0 && viewportW > 0 ? (imgNaturalH * viewportW) / imgNaturalW : 0
  );
  const overflow = $derived(Math.max(0, scaledImgH - viewportH));
  const clampedScroll = $derived(Math.max(0, Math.min(1, scroll)));
  const translateY = $derived(-overflow * clampedScroll);
</script>

{#snippet content()}
  <div
    bind:this={viewportEl}
    class="browser-screenshot-viewport"
    style="aspect-ratio: {contentAspect};"
  >
    <img
      bind:this={imgEl}
      {src}
      {alt}
      loading={imgLoading}
      decoding="async"
      onload={onImgLoad}
      style="transform: translate3d(0, {translateY}px, 0); will-change: transform;"
    />
  </div>
{/snippet}

<div class={className}>
  {#if frame === 'safari'}
    <SafariFrame {theme} {url} {secure} {flat}>
      {@render content()}
    </SafariFrame>
  {:else}
    <ChromeFrame {theme} {url} {tabTitle} {secure} {flat} {size}>
      {@render content()}
    </ChromeFrame>
  {/if}
</div>

<style>
  .browser-screenshot-viewport {
    width: 100%;
    overflow: hidden;
    position: relative;
    background: inherit;
  }

  .browser-screenshot-viewport :global(img) {
    display: block;
    width: 100%;
    height: auto;
    transition: transform 80ms linear;
  }
</style>
